import * as React from "react";
import { useParams } from "react-router-dom";
import { CalendarDatum, CalendarDayData, CalendarTooltipProps, ColorScale, ResponsiveCalendar } from "@nivo/calendar";
import * as moment from "moment";
import * as classnames from "classnames";
import { DiarySessionResult } from "./DiarySessionResult";
import { DiarySubjectSummaryPanel } from "./DiarySubjectSummaryPanel";
import { DiaryTrendGraph } from "./DiaryTrendGraph";
import { Datum, Point, Serie } from "@nivo/line";
import { WebPortalClient, Subject, DiarySession, Site, DiaryCompliance } from "webportalclient";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { Authorization } from "../../misc/components/Authorization";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { useSecurityContext } from "../../Core/SecurityContext";
import { orderBy, groupBy, Dictionary } from "lodash";
import { scaleQuantize } from 'd3-scale';
import { DiaryCalendarDayData, DiaryDayByDay } from "./DiaryDaybyDayCalendar";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import { SessionTypes } from "../../misc/api/ApiSessionTypes";
import { getDateRange } from "../../misc/math-functions";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter } from '@fortawesome/free-solid-svg-icons';

enum DiaryResultsTabs {
    PEF = "PEF",
    FEV1 = "FEV1"
}

export const DiaryResults = () => {
    const { id } = useParams<{ id: string }>();

    const [selectedDate, setSelectedDate] = React.useState<moment.Moment>();
    const [currentTab, setCurrentTab] = React.useState<DiaryResultsTabs>(DiaryResultsTabs.PEF);
    const [showFilter, setShowFilter] = React.useState(false);
    const client = useWebPortalApiClient();
    const legacyClient = useWebPortalLegacyClient();
    const [site, setSite] = React.useState<Site>();
    const [subject, setSubject] = React.useState<Subject>();
    const [allSessions, setAllSessions] = React.useState<DiarySession[]>();
    const [sessions, setSessions] = React.useState<DiarySession[]>([]);
    const [complianceRecords, setComplianceRecords] = React.useState<DiaryCompliance[]>();
    const [loadingSessions, setLoadingSessions] = React.useState<boolean>(false);
    const [calendarDays, setCalendarDays] = React.useState<CalendarDatum[]>([]);
    const [subjectLastKnownTimezone, setSubjectLastKnownTimezone] = React.useState<moment.Moment>();
    const [displaySettings, setDisplaySettings] = React.useState<{ displayPefTrend: boolean; displayFev1Trend: boolean; displayBaselines: boolean; displayCompliance: boolean }>();

    const { study } = useCurrentStudyConfigurationContext();
    const { currentUserPermissions } = useSecurityContext();

    React.useEffect(() => {
        if (displaySettings?.displayPefTrend && currentUserPermissions.find(x => x.Id === PermissionEnum.ViewDiaryTrends) !== undefined) {
            setCurrentTab(DiaryResultsTabs.PEF);
            return;
        }

        if (displaySettings?.displayFev1Trend && currentUserPermissions.find(x => x.Id === PermissionEnum.ViewDiaryTrends) !== undefined) {
            setCurrentTab(DiaryResultsTabs.FEV1);
        }
    }, [currentUserPermissions, displaySettings]);

    const calendarData = async (sessionsForCalendar: DiarySession[]) => {
        const groupedSessions = groupBy(sessionsForCalendar, s => moment.parseZone(s.occurred).format("YYYY-MM-DD"));

        const sortedSessions = sessionsForCalendar.sort((a, b) => moment.parseZone(a.occurred).valueOf() - moment.parseZone(b.occurred).valueOf());
        const firstSession = sortedSessions[0];
        const lastSession = sortedSessions[sortedSessions.length - 1];

        let dayByDayDays = getDateRange(firstSession.occurred, lastSession.occurred).map(d => ({
            day: moment.parseZone(d).format("YYYY-MM-DD"),
            value: 1
        } as CalendarDatum));

        const sessionsInSchedule = await client.client.getStudyScheduleSessions("id,label,studyScheduleVisitId,expectedMinimumQuestionnaires,expectedMinimumTests", `sessionTypeId eq '${SessionTypes.EDiary}' and disabled eq false and includedInDailySummaryCompliance eq true`);

        for (const [k, v] of Object.entries(groupedSessions)) {
            const groupedSessionsInSchedule = groupBy(sessionsInSchedule.value, s => s.studyScheduleVisitId);

            const completedVisits = [];
            for (const visit of v.map(x => x.visit)) {
                const sessionsInVisit = groupedSessionsInSchedule[visit.visitScheduleItemId];

                if (sessionsInVisit === undefined) {
                    completedVisits.push({ id: visit.id, completed: true });
                    continue;
                }

                const hasExpectedSessionsPerDay = v.length >= sessionsInVisit.length;

                const completeSessions = [];
                for (const session of sessionsInVisit) {

                    const expectedQuestionnaires = session.expectedMinimumQuestionnaires;
                    const expectedSpirometry = session.expectedMinimumTests;

                    let hasQuestionnaires = false;
                    if (expectedQuestionnaires === null || expectedQuestionnaires === undefined)
                        hasQuestionnaires = true;
                    else
                        hasQuestionnaires = v.find(x => x.name === session.label)?.questionnaires.length >= expectedQuestionnaires;

                    let hasExpectedSpirometry = false;
                    if (expectedSpirometry === 0 || expectedSpirometry === null || expectedSpirometry === undefined)
                        hasExpectedSpirometry = true;
                    else
                        hasExpectedSpirometry = v.find(x => x.name === session.label)?.tests.filter(t => t.accepted).length >= expectedSpirometry;

                    completeSessions.push({ id: session.id, completed: hasQuestionnaires && hasExpectedSpirometry && hasExpectedSessionsPerDay });
                }

                completedVisits.push({ id: visit.id, completed: completeSessions.filter(x => x.completed).length >= sessionsInVisit.length });
            }

            const normalisedValue = completedVisits.every(x => x.completed) ? 5 : 2;

            dayByDayDays = dayByDayDays.map(d => {
                if (d.day === moment(k).format("YYYY-MM-DD")) {
                    return {
                        day: d.day,
                        value: d.value + normalisedValue
                    } as CalendarDatum;
                } else {
                    return d;
                }
            });
        }

        setCalendarDays(dayByDayDays);
    }

    React.useEffect(() => {
        const getData = async () => {
            const diarySettings = await legacyClient.getStudyDiarySettings(study.id);
            setDisplaySettings({
                displayPefTrend: diarySettings.DisplayPefTrend,
                displayFev1Trend: diarySettings.DisplayFev1Trend,
                displayBaselines: diarySettings.DisplayBaselines,
                displayCompliance: diarySettings.DisplayCompliance,
            });

            const subjectResult = await client.client.getSubject(id);
            setSubject(subjectResult);

            const siteResult = await client.client.getSite(subjectResult.siteId);
            setSite(siteResult);

            const sessionResult = await client.client.getDiarySessions(undefined, `visit/subjectId eq ${id}`, undefined, "tests,questionnaires,visit");
            setAllSessions(sessionResult.value);

            const complianceResult = await client.client.getDiaryCompliances(undefined, `session/visit/subjectId eq ${id}`, "occurred desc");
            setComplianceRecords(complianceResult.value);

            const subjectTimeZone = moment.parseZone(sessionResult.value[sessionResult.value.length - 1].occurred);
            setSubjectLastKnownTimezone(subjectTimeZone);
            await calendarData(sessionResult.value);
        }

        getData();
    }, [client.client, id]);

    const GetScopedSessionData = async () => {
        if (selectedDate) {
            setLoadingSessions(true);

            const endOfDay = selectedDate.endOf('day').utcOffset(subjectLastKnownTimezone.utcOffset(), true).toISOString();
            const startOfDay = selectedDate.startOf('day').utcOffset(subjectLastKnownTimezone.utcOffset(), true).toISOString();
            const sessionResult = await client.client.getDiarySessions(undefined, `visit/subjectId eq ${id} and occurred ge ${startOfDay} and occurred le ${endOfDay}`, undefined, "visit,tests");
            setSessions(sessionResult.value);

            setLoadingSessions(false);
        }
    }

    React.useEffect(() => {
        GetScopedSessionData();
    }, [client.client, selectedDate, id]);


    const extractHighestFromMultipleSessions = (highestAcceptableGroupedPerDay: Dictionary<Datum[]>): Datum[] => {
        const highestAcceptable: Datum[] = [];
        for (const key in highestAcceptableGroupedPerDay) {
            const sessionsInDay = highestAcceptableGroupedPerDay[key];

            if (sessionsInDay.length === 0)
                highestAcceptable.push(sessionsInDay[0]);
            else {
                const maxValue = orderBy(sessionsInDay, [s => s.accepted, s => s.y], ["desc", "desc"])[0] as Datum;
                highestAcceptable.push(maxValue);
            }
        }
        return highestAcceptable.map(d => ({ x: d.x, y: d.y }));
    }

    const pefTrend = (): Serie[] => {
        if (!allSessions || allSessions.map(s => s.tests).flat().length === 0) return [];

        const highestAcceptableGroupedPerDay = groupBy(allSessions.map(s => ({
            x: moment.parseZone(s.occurred).format("YYYY-MM-DD"),
            accepted: s.tests.length > 0 ? orderBy(s.tests, [a => a.accepted, b => b.fev1], ["desc", "desc"])[0].accepted : undefined,
            y: s.tests.length > 0 ? orderBy(s.tests, [a => a.accepted, b => b.pef], ["desc", "desc"])[0].pef : undefined
        })).filter(d => d.y), v => v.x);

        const highestAcceptable = extractHighestFromMultipleSessions(highestAcceptableGroupedPerDay);

        return [{
            id: "Highest PEF",
            color: "#ff0000",
            data: orderBy(highestAcceptable, [a => a.x], ["asc"]),
        }];
    }

    const memorizedPefTrend = React.useMemo(pefTrend, [allSessions]);

    const fev1Trend = (): Serie[] => {
        if (!allSessions || allSessions.map(s => s.tests).flat().length === 0) return [];

        const highestAcceptableGroupedPerDay = groupBy(allSessions.map(s => ({
            x: moment.parseZone(s.occurred).format("YYYY-MM-DD"),
            accepted: s.tests.length > 0 ? orderBy(s.tests, [a => a.accepted, b => b.fev1], ["desc", "desc"])[0].accepted : undefined,
            y: s.tests.length > 0 ? orderBy(s.tests, [a => a.accepted, b => b.fev1], ["desc", "desc"])[0].fev1 : undefined,
        })).filter(d => d.y), v => v.x);

        const highestAcceptable = extractHighestFromMultipleSessions(highestAcceptableGroupedPerDay);

        return [{
            id: "Highest FEV1",
            color: "#ff0000",
            data: orderBy(highestAcceptable, [a => a.x], ["asc"])
        }];
    }

    const memorizedfev1Trend = React.useMemo(fev1Trend, [allSessions]);

    if (!subject || !allSessions) {
        return <LoadingWithMessage message="Loading Diary Results..." />
    }

    const onCalendarClick = (datum: DiaryCalendarDayData) => {
        if (datum.color === "#fff" || datum.color === "#e30613")
            return;

        setSelectedDate(moment.parseZone(datum.date).utc(true));
    };

    const onTrendClick = (e: Point) => {
        setSelectedDate(moment.parseZone(e.data.x.toString()).utc(true));
    };

    const diaryTrendTabsContent = () => {
        return (
            <div className="tab-container">
                {displaySettings.displayPefTrend && currentTab === DiaryResultsTabs.PEF ? (
                    <>
                        {memorizedPefTrend.length > 0 ? <DiaryTrendGraph
                            data={memorizedPefTrend}
                            onPointClick={onTrendClick}
                            showFilter={showFilter}
                            onShowFilter={s => setShowFilter(s)}
                            tooltip={({ point }) => {
                                return <div className="lineChartTooltip">
                                    <strong>{moment.parseZone(point.data.x).format("DD-MMM-YYYY")}</strong> PEF: <strong>{point.data.y as number}</strong>
                                </div>
                            }}
                            yAxisLabel="PEF (L/min)"
                            markers={subject.baselinePef ? [
                                {
                                    axis: 'y',
                                    value: subject.baselinePef,
                                    legend: 'Baseline PEF',
                                    lineStyle: {
                                        stroke: 'red',
                                    },
                                    textStyle: {
                                        fill: 'red',
                                    },
                                }
                            ] : undefined}
                            baseline={subject.baselinePef}
                        /> : <Alert show={true} category={AlertCategoryEnum.Info}>
                            There is no data to trend
                        </Alert>}
                    </>
                ) : null}
                {displaySettings.displayFev1Trend && currentTab === DiaryResultsTabs.FEV1 ? (
                    <>
                        {memorizedfev1Trend.length > 0 ? <DiaryTrendGraph
                            data={memorizedfev1Trend}
                            onPointClick={onTrendClick}
                            showFilter={showFilter}
                            onShowFilter={s => setShowFilter(s)}
                            tooltip={({ point }) => {
                                return <div className="lineChartTooltip">
                                    <strong>{moment(point.data.x).format("DD-MMM-YYYY")}</strong> FEV1: <strong>{point.data.y as number}</strong>
                                </div>
                            }}
                            yAxisLabel="FEV1 (L)"
                        /> : <Alert show={true} category={AlertCategoryEnum.Info}>
                            There is no data to trend
                        </Alert>}
                    </>
                ) : null}
            </div>
        );
    }

    const pefTrendResults = pefTrend();

    const resultGraphsPanel = () => {
        if (allSessions.length === 0) {
            return <Alert show={true} category={AlertCategoryEnum.Info}>There are no Diary results for this subject</Alert>
        }

        return (
            <Authorization currentPermissions={currentUserPermissions} studyId={study.id} permissionId={PermissionEnum.ViewDiaryTrends}>
                {displaySettings.displayFev1Trend || displaySettings.displayPefTrend ? <div className="panel panel-default" data-automationid="DiaryTrendPanel">
                    <div className="panel-body">
                        <ul className="nav nav-tabs">
                            {displaySettings.displayPefTrend ? <li className={classnames("nav-item", {
                                active: currentTab === DiaryResultsTabs.PEF
                            })}>
                                <a
                                    className="nav-link"
                                    onClick={() => setCurrentTab(DiaryResultsTabs.PEF)}
                                >
                                    PEF Trend
                                </a>
                            </li> : null}
                            {displaySettings.displayFev1Trend ? <li className={classnames("nav-item", {
                                active: currentTab === DiaryResultsTabs.FEV1
                            })}>
                                <a
                                    className="nav-link"
                                    onClick={() => setCurrentTab(DiaryResultsTabs.FEV1)}
                                >
                                    FEV1 Trend
                                </a>
                            </li> : null}
                            <div className="pull-right">
                                <button className="btn btn-primary" disabled={pefTrendResults.length === 0} onClick={() => setShowFilter(true)}>
                                    <FontAwesomeIcon icon={faFilter} />
                                </button>
                            </div>
                        </ul>
                        {diaryTrendTabsContent()}
                    </div>
                </div> : null}
            </Authorization>
        );
    }

    const minDate = pefTrendResults.length !== 0 ? moment.min(...pefTrendResults[0].data.map(d => moment.parseZone(d.x))) : moment();
    const maxDate = pefTrendResults.length !== 0 ? moment.max(...pefTrendResults[0].data.map(d => moment.parseZone(d.x))) : moment();

    const calendarColorRange = ["#ffffff", "#e30613", "#f28b16", "#f28b16", "#f28b16", "#f28b16", "#37a537"];

    const colorScale = (): ColorScale => {
        const defaultColorScale = scaleQuantize<string>()
            .domain([0, 6])
            .range(calendarColorRange);

        const intColorScale = value => {
            const number = parseInt(value)
            return defaultColorScale(number)
        }
        intColorScale.ticks = count => {
            return [0, 1, 2, 6];
        }

        return intColorScale;
    }

    const complianceSummary = () => {
        if (allSessions.length === 0) {
            return null;
        }

        const years = groupBy(allSessions, s => moment.parseZone(s.occurred).format("YYYY"));
        const totalYears = allSessions ? Object.keys(years).length : 0;

        return (
            <div className="panel panel-default" data-automationid="DayByDaySummaryPanel">
                <div className="panel-heading">
                    <h4>Daily Summary</h4>
                </div>
                <div className="panel-body">
                    <div id="calendarWrapper" className="col-sm-12" style={{ height: (totalYears * 350) + "px" }}>
                        <DiaryDayByDay calendarData={calendarDays}
                            minDate={minDate}
                            maxDate={maxDate}
                            calendarColorRange={calendarColorRange}
                            colorScale={colorScale}
                            onCalendarClick={onCalendarClick}
                        />
                    </div>
                </div>
            </div>
        );
    }

    const dates = groupBy(allSessions, s => moment.parseZone(s.occurred).format("YYYY-MM-DD"));
    const totalDays = allSessions ? Object.keys(dates).length : 0;

    return (
        <>
            <div className="row">
                <div className="col-sm-12">
                    <h2 className="text-primary">Diary Results</h2>
                </div>
            </div>

            <DiarySubjectSummaryPanel
                subjectId={subject.id}
                siteNumber={site ? site.number : ""}
                subjectNumber={subject.number}
                numberOfDays={totalDays}
                complianceRecords={complianceRecords}
                displayBaselines={displaySettings.displayBaselines}
                displayCompliance={displaySettings.displayCompliance}
            />

            {resultGraphsPanel()}

            {complianceSummary()}

            {selectedDate ? (
                <DiarySessionResult
                    subject={subject}
                    selectedDate={selectedDate.toISOString(true)}
                    sessions={sessions}
                    loadingScopedSessions={loadingSessions}
                    onClose={() => setSelectedDate(undefined)}
                />
            ) : null}
        </>
    );
}
